import React, { useState } from 'react';
import { useSeason } from '../../../context/SeasonContext';
import { useAuth } from '../../../context/AuthContext';
import { supabase } from '../../../supabaseClient';
import { Plus, ChevronRight, AlertTriangle, CheckCircle, Clock, Trophy, Star, Lock } from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_ORDER = ['upcoming', 'registration', 'active', 'playoffs', 'completed'];

const STATUS_META = {
    upcoming: {
        label: 'Upcoming',
        color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
        dot: 'bg-gray-400',
        icon: Clock,
    },
    registration: {
        label: 'Registration Open',
        color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
        dot: 'bg-blue-500',
        icon: Star,
    },
    active: {
        label: 'Active Season',
        color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
        dot: 'bg-green-500 animate-pulse',
        icon: CheckCircle,
    },
    playoffs: {
        label: 'Playoffs',
        color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
        dot: 'bg-orange-500 animate-pulse',
        icon: Trophy,
    },
    completed: {
        label: 'Completed',
        color: 'bg-gray-100 text-gray-500 dark:bg-gray-700/50 dark:text-gray-400',
        dot: 'bg-gray-300',
        icon: Lock,
    },
};

// What label to show on the transition button for each current status
const NEXT_ACTION_LABEL = {
    upcoming:     'Open Registration',
    registration: 'Start Season',
    active:       'Begin Playoffs',
    playoffs:     'Mark Completed',
};

// Which transitions are considered destructive (require confirmation)
const DESTRUCTIVE_TRANSITIONS = new Set(['playoffs→completed', 'active→playoffs']);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function nextStatus(current) {
    const idx = STATUS_ORDER.indexOf(current);
    return idx >= 0 && idx < STATUS_ORDER.length - 1 ? STATUS_ORDER[idx + 1] : null;
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    // Parse as local date to avoid UTC shift
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
    });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
    const meta = STATUS_META[status] ?? STATUS_META.upcoming;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
        </span>
    );
};

// Confirmation modal for destructive transitions
const ConfirmModal = ({ season, toStatus, onConfirm, onCancel, loading }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="text-amber-500 mt-0.5 shrink-0" size={22} />
                <div>
                    <h3 className="font-bold text-gray-900 dark:text-white text-lg">
                        Confirm status change
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                        You're moving <strong>{season.name}</strong> from{' '}
                        <strong>{STATUS_META[season.status]?.label}</strong> to{' '}
                        <strong>{STATUS_META[toStatus]?.label}</strong>.
                    </p>
                    {toStatus === 'completed' && (
                        <p className="text-amber-600 dark:text-amber-400 text-sm mt-2 font-medium">
                            This cannot be undone. The season will be locked.
                        </p>
                    )}
                </div>
            </div>
            <div className="flex gap-3 justify-end">
                <button
                    onClick={onCancel}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                >
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition disabled:opacity-50"
                >
                    {loading ? 'Updating…' : 'Confirm'}
                </button>
            </div>
        </div>
    </div>
);

// Create season modal
const CreateSeasonModal = ({ onClose, onCreated, adminId }) => {
    const [name, setName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleCreate = async () => {
        if (!name.trim()) { setError('Season name is required.'); return; }
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/admin-actions`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                    },
                    body: JSON.stringify({
                        action: 'createSeason',
                        adminId,
                        payload: { name: name.trim(), startDate: startDate || null },
                    }),
                }
            );
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error ?? 'Failed to create season');
            onCreated();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
                <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-4">
                    Create new season
                </h3>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold mb-1.5 dark:text-white">
                            Season name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="e.g. Season 4"
                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold mb-1.5 dark:text-white">
                            Start date <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                    </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                    New seasons open in <strong>Registration</strong> status. The current active season is unaffected.
                </p>

                <div className="flex gap-3 justify-end mt-5">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCreate}
                        disabled={loading || !name.trim()}
                        className="px-4 py-2 text-sm font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition disabled:opacity-50"
                    >
                        {loading ? 'Creating…' : 'Create season'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Individual season card
const SeasonCard = ({ season, onTransition }) => {
    const meta = STATUS_META[season.status] ?? STATUS_META.upcoming;
    const Icon = meta.icon;
    const next = nextStatus(season.status);
    const actionLabel = next ? NEXT_ACTION_LABEL[season.status] : null;
    const isCompleted = season.status === 'completed';

    return (
        <div className={`rounded-xl border dark:border-gray-700 bg-white dark:bg-gray-800 p-5 flex flex-col gap-4 ${isCompleted ? 'opacity-60' : ''}`}>
            {/* Top row: name + badge */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${meta.color}`}>
                        <Icon size={18} />
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white text-lg leading-tight">
                        {season.name}
                    </h3>
                </div>
                <StatusBadge status={season.status} />
            </div>

            {/* Date info */}
            <div className="flex gap-6 text-sm text-gray-500 dark:text-gray-400">
                <div>
                    <span className="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">
                        Start date
                    </span>
                    {formatDate(season.start_date)}
                </div>
                {season.end_date && (
                    <div>
                        <span className="block text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">
                            End date
                        </span>
                        {formatDate(season.end_date)}
                    </div>
                )}
            </div>

            {/* Transition button */}
            {actionLabel && (
                <div className="pt-1 border-t dark:border-gray-700">
                    <button
                        onClick={() => onTransition(season, next)}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition
                            ${next === 'completed'
                                ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:hover:bg-amber-900/40 border border-amber-200 dark:border-amber-800'
                                : 'bg-orange-600 text-white hover:bg-orange-700'
                            }`}
                    >
                        {actionLabel}
                        <ChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

const SeasonManagementPanel = () => {
    const { seasons, loadSeasons } = useSeason();
    const { user } = useAuth();
    const adminId = user?.id;

    const [showCreate, setShowCreate] = useState(false);
    const [confirm, setConfirm] = useState(null); // { season, toStatus }
    const [transitioning, setTransitioning] = useState(false);
    const [error, setError] = useState(null);

    // Sort: active/playoffs first, then registration, then upcoming, then completed
    const sorted = [...seasons].sort((a, b) => {
        const priority = { playoffs: 0, active: 1, registration: 2, upcoming: 3, completed: 4 };
        return (priority[a.status] ?? 5) - (priority[b.status] ?? 5);
    });

    const handleTransitionClick = (season, toStatus) => {
        const key = `${season.status}→${toStatus}`;
        if (DESTRUCTIVE_TRANSITIONS.has(key)) {
            setConfirm({ season, toStatus });
        } else {
            executeTransition(season, toStatus);
        }
    };

    const executeTransition = async (season, toStatus) => {
        setTransitioning(true);
        setError(null);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/admin-actions`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                    },
                    body: JSON.stringify({
                        action: 'updateSeasonStatus',
                        adminId,
                        payload: { seasonId: season.id, newStatus: toStatus },
                    }),
                }
            );
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error ?? 'Transition failed');
            await loadSeasons();
        } catch (err) {
            setError(err.message);
        } finally {
            setTransitioning(false);
            setConfirm(null);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Season Management</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        Create seasons and advance their lifecycle. Status changes are permanent and logged.
                    </p>
                </div>
                <button
                    onClick={() => setShowCreate(true)}
                    className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2.5 rounded-lg hover:bg-orange-700 transition text-sm font-semibold"
                >
                    <Plus size={18} />
                    New season
                </button>
            </div>

            {/* Error banner */}
            {error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg text-sm">
                    <AlertTriangle size={16} className="shrink-0" />
                    {error}
                    <button
                        onClick={() => setError(null)}
                        className="ml-auto text-red-400 hover:text-red-600"
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Status key */}
            <div className="flex flex-wrap gap-2 text-xs">
                <span className="text-gray-400 dark:text-gray-500 self-center">Lifecycle:</span>
                {STATUS_ORDER.map((s, i) => (
                    <React.Fragment key={s}>
                        <StatusBadge status={s} />
                        {i < STATUS_ORDER.length - 1 && (
                            <ChevronRight size={14} className="text-gray-300 dark:text-gray-600 self-center" />
                        )}
                    </React.Fragment>
                ))}
            </div>

            {/* Season cards */}
            {seasons.length === 0 ? (
                <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                    No seasons yet. Create one to get started.
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sorted.map(season => (
                        <SeasonCard
                            key={season.id}
                            season={season}
                            onTransition={handleTransitionClick}
                        />
                    ))}
                </div>
            )}

            {/* Modals */}
            {showCreate && (
                <CreateSeasonModal
                    adminId={adminId}
                    onClose={() => setShowCreate(false)}
                    onCreated={async () => {
                        await loadSeasons();
                        setShowCreate(false);
                    }}
                />
            )}

            {confirm && (
                <ConfirmModal
                    season={confirm.season}
                    toStatus={confirm.toStatus}
                    loading={transitioning}
                    onConfirm={() => executeTransition(confirm.season, confirm.toStatus)}
                    onCancel={() => setConfirm(null)}
                />
            )}
        </div>
    );
};

export default SeasonManagementPanel;